import { motion } from "framer-motion";
import { HUB_NODES } from "../../data/site";
import { EASE } from "../../lib/motion";

export default function HubDiagram() {
  const N = HUB_NODES.length;
  const nodes = HUB_NODES.map((n, i) => {
    const angle = (Math.PI * 2 * i) / N - Math.PI / 2;
    return { ...n, x: 50 + 41 * Math.cos(angle), y: 50 + 40 * Math.sin(angle) };
  });

  return (
    <div className="mx-auto w-full" data-testid="hub-diagram">
      <div className="relative mx-auto hidden aspect-square w-full max-w-[560px] md:block">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          {nodes.map((n, i) => (
            <motion.line
              key={n.label}
              x1="50"
              y1="50"
              x2={n.x}
              y2={n.y}
              stroke="hsl(199 89% 60%)"
              strokeOpacity="0.35"
              strokeWidth="0.28"
              strokeDasharray="1.6 1.6"
              className="animate-dash-flow"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.18 * i, ease: "easeOut" }}
            />
          ))}
        </svg>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
        >
          <div className="relative flex flex-col items-center gap-2.5 rounded-3xl border border-electric/30 bg-card px-7 py-6 shadow-glow-sm">
            <span className="absolute inset-0 -z-10 rounded-3xl bg-electric/10 blur-xl" aria-hidden="true" />
            <img
              src="/dasoft-logo.png"
              alt="CoopSys CoreBank — DASOFT SRL"
              className="h-9 w-auto dark:rounded-lg dark:bg-white dark:px-2.5 dark:py-2"
            />
            <div className="text-center">
              <p className="font-display text-base font-bold">CoopSys CoreBank</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Núcleo central</p>
            </div>
            <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse-dot rounded-full bg-electric" aria-hidden="true" />
          </div>
        </motion.div>

        {nodes.map((n, i) => (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, delay: 0.5 + i * 0.1, ease: EASE }}
            className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border border-border bg-card/90 px-3.5 py-2 text-xs font-medium shadow-card backdrop-blur"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            <n.icon className="h-3.5 w-3.5 text-electric" strokeWidth={1.8} />
            {n.label}
          </motion.div>
        ))}
      </div>

      <div className="flex flex-col items-center gap-6 md:hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-col items-center gap-2 rounded-3xl border border-electric/30 bg-card px-8 py-5 shadow-glow-sm"
        >
          <img
            src="/dasoft-logo.png"
            alt="CoopSys CoreBank — DASOFT SRL"
            className="h-8 w-auto dark:rounded-lg dark:bg-white dark:px-2.5 dark:py-2"
          />
          <p className="font-display text-sm font-bold">CoopSys CoreBank</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Núcleo central</p>
        </motion.div>
        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
          className="flex flex-wrap justify-center gap-2.5"
        >
          {HUB_NODES.map((n) => (
            <motion.li
              key={n.label}
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } } }}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2 text-xs font-medium"
            >
              <n.icon className="h-3.5 w-3.5 text-electric" strokeWidth={1.8} />
              {n.label}
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </div>
  );
}
